import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  TextField,
  MenuItem,
  IconButton,
  Divider,
} from '@mui/material';
import { Close, Inventory } from '@mui/icons-material';

const categories = ['Linens', 'Toiletries', 'Cleaning', 'Food & Beverage', 'Minibar', 'Supplies', 'Other'];
const units = ['pieces', 'sets', 'bottles', 'rolls', 'cans', 'boxes', 'kg', 'liters', 'pods'];

export default function InventoryItemDialog({ open, onClose, item, onSave, mode = 'add' }) {
  const [formData, setFormData] = useState({
    name: '',
    category: 'Supplies',
    quantity: '',
    minStock: '',
    unit: 'pieces',
    supplier: '',
    cost: '',
    lastRestocked: new Date().toISOString().split('T')[0],
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (item && mode === 'edit') {
      setFormData({ ...item });
    } else if (mode === 'add') {
      setFormData({
        name: '',
        category: 'Supplies',
        quantity: '',
        minStock: '',
        unit: 'pieces',
        supplier: '',
        cost: '',
        lastRestocked: new Date().toISOString().split('T')[0],
      });
    }
  }, [item, mode]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Item name is required';
    if (!formData.quantity || parseInt(formData.quantity) < 0) {
      newErrors.quantity = 'Valid quantity is required';
    }
    if (!formData.minStock || parseInt(formData.minStock) <= 0) {
      newErrors.minStock = 'Valid minimum stock is required';
    }
    if (!formData.cost || parseFloat(formData.cost) <= 0) {
      newErrors.cost = 'Valid cost is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const itemData = {
      ...formData,
      quantity: parseInt(formData.quantity),
      minStock: parseInt(formData.minStock),
      cost: parseFloat(formData.cost),
      id: item?.id || `INV${String(Math.floor(Math.random() * 10000)).padStart(3, '0')}`,
    };

    onSave(itemData);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      name: '',
      category: 'Supplies',
      quantity: '',
      minStock: '',
      unit: 'pieces',
      supplier: '',
      cost: '',
      lastRestocked: new Date().toISOString().split('T')[0],
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Inventory color="primary" />
            <Typography variant="h6">
              {mode === 'add' ? 'Add Inventory Item' : 'Edit Inventory Item'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Item Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Item Name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              placeholder="e.g., Bed Linens (Queen)"
              required
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              select
              label="Category"
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
            >
              {categories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Current Quantity"
              type="number"
              value={formData.quantity}
              onChange={(e) => handleChange('quantity', e.target.value)}
              error={!!errors.quantity}
              helperText={errors.quantity}
              required
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Minimum Stock"
              type="number"
              value={formData.minStock}
              onChange={(e) => handleChange('minStock', e.target.value)}
              error={!!errors.minStock}
              helperText={errors.minStock}
              required
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              select
              label="Unit"
              value={formData.unit}
              onChange={(e) => handleChange('unit', e.target.value)}
            >
              {units.map((unit) => (
                <MenuItem key={unit} value={unit}>
                  {unit}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
              Supplier & Cost
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Supplier Name"
              value={formData.supplier}
              onChange={(e) => handleChange('supplier', e.target.value)}
              placeholder="e.g., Luxury Textiles Co."
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Unit Cost"
              type="number"
              value={formData.cost}
              onChange={(e) => handleChange('cost', e.target.value)}
              error={!!errors.cost}
              helperText={errors.cost}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
              }}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Last Restocked Date"
              type="date"
              value={formData.lastRestocked}
              onChange={(e) => handleChange('lastRestocked', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          {mode === 'add' ? 'Add Item' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
